import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { getShiftDefinitions } from "../../apis/shiftDefinition";
import { updateShiftAssignment } from "../../apis/shiftAssignment";
import { formatTimeToAmPm } from "../../utils/DateUtil";

interface ChangeShiftModalProps {
  assignmentId: number;
  userName: string;
  currentShiftId: number;
  onSuccess: () => void;
  onClose: () => void;
}

const ChangeShiftModal: React.FC<ChangeShiftModalProps> = ({
  assignmentId,
  userName,
  currentShiftId,
  onSuccess,
  onClose,
}) => {
  const { showNotification } = useNotification();
  const [shifts, setShifts] = useState<any[]>([]);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(currentShiftId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getShiftDefinitions({ per_page: 100 }).then((res: any) => {
      setShifts(res.data?.data || res.data || []);
    });
    setSelectedShiftId(currentShiftId);
  }, [currentShiftId]);

  const handleSave = async () => {
    if (!selectedShiftId || selectedShiftId === currentShiftId) {
      showNotification("warning", "Please select a different shift", "", "topRight");
      return;
    }
    setSaving(true);
    try {
      await updateShiftAssignment(assignmentId, { shiftDefinitionId: selectedShiftId });
      showNotification("success", "Shift updated successfully", "", "topRight");
      onSuccess();
      onClose();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to update shift",
        err?.response?.data?.message || "",
        "topRight"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Change shift for <span className="font-medium text-gray-900">{userName}</span>
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">New Shift</label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select shift"
          value={selectedShiftId ?? undefined}
          onChange={(v) => setSelectedShiftId(v)}
          options={shifts.map((s) => ({
            value: s.id,
            label: `${s.name} (${formatTimeToAmPm(s.startTime ?? "")} - ${formatTimeToAmPm(s.endTime ?? "")})`,
          }))}
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !selectedShiftId || selectedShiftId === currentShiftId}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Change Shift"}
        </button>
      </div>
    </div>
  );
};

export default ChangeShiftModal;
